import React, { useState, useMemo, useEffect } from 'react';
import { getStructuresForPortal, calculatePortalKPIs, type PortalStructureCache, extractStructureName, extractStructureType, extractLocality, calculateCompliance, extractAccessibilityScoresFromStructure, getTotalScorePercent, getCanonicalStructureId } from '../utils/portalStructures';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import { Building2, BarChart3, FileText, Search, Filter, User, LogOut, Settings, TrendingUp, AlertTriangle, CheckCircle, Lightbulb, ArrowRight, Star, Accessibility, Eye, Users, Utensils } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import PortalHeader from './portal/PortalHeader';
import { Introduction } from './portal/Introduction';
import KPIStatCard from './portal/KPIStatCard';
import PerformanceTrendsChart from './portal/PerformanceTrendsChart';
import PopularSuggestions from './portal/PopularSuggestions';
import { NeedsAttentionCard, PortalOverviewCard, TopPerformersCard } from './portal/SummaryCards';
import { getSuggestionsForStructures, type Suggestion } from '@/lib/suggestions';
import { PortalMonitorV2 } from './portal/PortalMonitorV2';
import { isSuggestionsV2Enabled } from '@/lib/env';
import { getPortalBranding, getPriorityPortal } from '../utils/portalPriority';

interface PortalManagerDashboardProps {
  onLogout?: () => void;
  // both handlers now receive a structure slug filename (eg "accademia-carrara-culturale-museo.json")
  onViewPassport?: (structureSlug: string) => void;
  onViewMonitor?: (structureData: any, portalName: string) => void;
  onViewImprovementSuggestions?: () => void;
  user?: any;
  managedStructures?: any[];
  portals?: string[];
  portalStructureCache?: PortalStructureCache;
  portalStructuresError?: { code: string } | null;
  onGoHome: () => void; // 👈 new
  onSwitchToStructure?: () => void;
  initialActiveSection?: "overview" | "details" | "monitora"; // 👈 add this prop
  onRetry?: () => Promise<void>; // 👈 new retry callback
}

export function PortalManagerDashboard({ onLogout, onViewPassport, onViewMonitor, onViewImprovementSuggestions, user, managedStructures, portals, portalStructureCache, portalStructuresError, onGoHome, onSwitchToStructure, initialActiveSection, onRetry }: PortalManagerDashboardProps) {
  const [activeSection, setActiveSection] = useState(initialActiveSection || "overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [mainCategoryFilter, setMainCategoryFilter] = useState("all");
  const [subCategoryFilter, setSubCategoryFilter] = useState("all");
  const [chartCategoryFilter, setChartCategoryFilter] = useState("all");
  const [chartGranularity, setChartGranularity] = useState("category");
  const [structuresListOverview, setStructuresListOverview] = useState<any[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoadingStructures, setIsLoadingStructures] = useState(false);
  const [structuresLoadError, setStructuresLoadError] = useState<string | null>(null);
  const [suggestionsByUid, setSuggestionsByUid] = useState<Record<string, Suggestion[]>>({});
  const safePortals = Array.isArray(portals) ? portals : [];
  const portalName = getPriorityPortal(safePortals) || "Visit Bergamo";
  const isVisitBergamo = getPortalBranding(safePortals, true) === "visit-bergamo";

  const hasNumericValues = (value: unknown): boolean => {
    if (!value || typeof value !== 'object') return false;
    return Object.values(value as Record<string, unknown>).some((entry) => typeof entry === 'number' && !Number.isNaN(entry));
  };

  const getScoresPresence = (structure: any) => {
    const scorePercent = structure?.score_percent ?? structure?.structure?.score_percent;
    const verticalScores = structure?.vertical_scores_percent ?? structure?.structure?.vertical_scores_percent;
    const privateScores = structure?.private_scores ?? structure?.structure?.private_scores;

    return {
      score_percent: typeof scorePercent === 'number' && !Number.isNaN(scorePercent),
      vertical_scores_percent: hasNumericValues(verticalScores),
      private_scores: hasNumericValues(privateScores)
    };
  };

  const normalizeStructureName = (value: string): string => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[.,;:!?'"()\[\]{}<>]/g, '')
      .replace(/\s+/g, ' ');
  };

  const getStableStructureKey = (item: any, index: number): string => {
    const canonicalId = getCanonicalStructureId(item);
    if (canonicalId) return canonicalId;
    const rawName = item?.name || '';
    const normalizedName = normalizeStructureName(String(rawName || 'unknown'));
    const slug = item?.slug || '';
    return `${normalizedName}::${slug || index}`;
  };

  // Calculate dynamic KPIs from cached structures
  const portalKPIs = useMemo(() => {
    if (portalStructureCache && portalName) {
      const cachedStructures = getStructuresForPortal(portalStructureCache, portalName);
      return calculatePortalKPIs(cachedStructures);
    }
    // Return null when data is loading instead of fallback values
    return null;
  }, [portalStructureCache, portalName]);

  const verticalConfig = useMemo(() => ([
    {
      key: 'motoria',
      label: 'Motoria',
      icon: Accessibility,
      iconBgClassName: 'bg-blue-100',
      iconClassName: 'text-blue-600',
      valueClassName: 'text-blue-600',
      delta: '+5.2%'
    },
    {
      key: 'sensoriale',
      label: 'Sensoriale',
      icon: Eye,
      iconBgClassName: 'bg-orange-100',
      iconClassName: 'text-orange-600',
      valueClassName: 'text-orange-600',
      delta: '+2.8%'
    },
    {
      key: 'cognitiva',
      label: 'Cognitiva',
      icon: Users,
      iconBgClassName: 'bg-cyan-100',
      iconClassName: 'text-cyan-600',
      valueClassName: 'text-cyan-600',
      delta: '+1.4%'
    },
    {
      key: 'alimentare',
      label: 'Alimentare',
      icon: Utensils,
      iconBgClassName: 'bg-green-100',
      iconClassName: 'text-green-600',
      valueClassName: 'text-green-600',
      delta: '+3.6%'
    }
  ]), []);

  const verticalKeys = useMemo(() => verticalConfig.map((item) => item.key), [verticalConfig]);
  const getVerticalAverage = (accessibility: Record<string, number>) => {
    const scores = verticalKeys.map((key) => accessibility?.[key] || 0);
    return scores.reduce((sum, score) => sum + score, 0) / (scores.length || 1);
  };

  // Create baseline performance trends data using actual KPI values
  const performanceTrendsData = useMemo(() => [
    { month: "Mag", motoria: 77, sensoriale: 77, cognitiva: 72, alimentare: 85 },
    { month: "Giu", motoria: 78, sensoriale: 79, cognitiva: 72, alimentare: 86 },
    { month: "Lug", motoria: 79, sensoriale: 80, cognitiva: 73, alimentare: 87 },
    { month: "Ago", motoria: 80, sensoriale: 81, cognitiva: 73, alimentare: 88 },
    { month: "Set", motoria: 81, sensoriale: 82, cognitiva: 74, alimentare: 89 },
    { month: "Ott", motoria: 82, sensoriale: 82, cognitiva: 74, alimentare: 89 },
  ], []);

  // Track if any improvements have been made (for future use)
  const hasImprovements = false;

  // Dynamic KPIs based on actual data
  const kpis = portalKPIs ? verticalConfig.map((item) => {
    const percentValue = (portalKPIs as any)?.[item.key] ?? 0;
    return {
      iconBgClassName: item.iconBgClassName,
      icon: <item.icon className={`w-6 h-6 ${item.iconClassName}`} />,
      label: item.label,
      value: `${percentValue}%`,
      delta: item.delta,
      valueClassName: getComplianceColorClass(percentValue),
    };
  }) : [];

  // Use cached portal structures instead of fetching from index
  useEffect(() => {
    // Add a minimum loading time to ensure loading state is visible
    const minLoadingTime = 1500; // 1.5 seconds minimum loading
    const startTime = Date.now();
    
    const processData = () => {
      setIsLoadingStructures(true);
      setStructuresLoadError(null);
      
      try {
        if (portalStructureCache && portalName) {
          const cachedStructures = getStructuresForPortal(portalStructureCache, portalName);
          
          // Transform cached structures to the expected format
          const transformedStructures = cachedStructures.map((structure, index) => {
            const meta = structure._meta || {};
            const scoresPresence = getScoresPresence(structure);
            
            // Use pre-computed meta data if available, otherwise extract
            let name = meta.name || extractStructureName(structure);
            let type = meta.type || extractStructureType(structure);
            let locality = meta.locality || extractLocality(structure);
            let compliance = meta.compliance || calculateCompliance(structure);
            let accessibility = meta.accessibility || extractAccessibilityScoresFromStructure(structure);
            let totalScorePercent = getTotalScorePercent(structure);
            
            return {
              id: structure.id || String(index + 1),
              id_backend: (structure as any).id_backend,
              backend_id: (structure as any).backend_id,
              // Prefer _filename from Firestore enrichment when available (real public slug)
              slug: (structure as any)._filename || meta.filename || meta.slug || structure.id || '',
              name: name || `Struttura ${index + 1}`,
              type: type || 'Sconosciuto',
              compliance: compliance,
              locality: locality || '',
              accessibility: accessibility,
              totalScorePercent: totalScorePercent,
              scoresPresence: scoresPresence,
              legacySuggestions: Array.isArray((structure as any).suggestions)
                ? (structure as any).suggestions
                : undefined
            };
          });
          
          // Ensure minimum loading time has passed before showing data
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            setStructuresListOverview(transformedStructures);
            setIsLoadingStructures(false);
            setInitialLoading(false);
          }, remainingTime);
        } else if (portalStructuresError) {
          // Handle error from props
          const errorMsg = typeof portalStructuresError === 'object' && 'code' in portalStructuresError
            ? `Errore: ${portalStructuresError.code}`
            : 'Errore nel caricamento delle strutture';
          setStructuresLoadError(errorMsg);
          setStructuresListOverview([]);
          
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            setIsLoadingStructures(false);
            setInitialLoading(false);
          }, remainingTime);
        } else {
          setStructuresListOverview([]);
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            setIsLoadingStructures(false);
            setInitialLoading(false);
          }, remainingTime);
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'Errore sconosciuto nel caricamento';
        setStructuresLoadError(errorMsg);
        setStructuresListOverview([]);
        
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          setIsLoadingStructures(false);
          setInitialLoading(false);
        }, remainingTime);
      }
    };
    
    processData();
  }, [portalStructureCache, portalName, portalStructuresError]);

  // Helper functions to parse structure types
  const parseStructureType = (type: string) => {
    if (!type) return { mainCategory: 'altro', subCategory: '' };
    
    const normalized = type.toLowerCase();
    
    // Check for main categories
    if (normalized.includes('culturale')) {
      const parts = type.split(':');
      return {
        mainCategory: 'culturale',
        subCategory: parts.length > 1 ? parts[1].trim() : ''
      };
    } else if (normalized.includes('ricettiva')) {
      const parts = type.split(':');
      return {
        mainCategory: 'ricettiva', 
        subCategory: parts.length > 1 ? parts[1].trim() : ''
      };
    } else if (normalized.includes('ristorazione')) {
      const parts = type.split(':');
      return {
        mainCategory: 'ristorazione',
        subCategory: parts.length > 1 ? parts[1].trim() : ''
      };
    } else {
      return { mainCategory: 'altro', subCategory: type };
    }
  };

  // Generate unique categories from actual data with counts
  const categoryData = useMemo(() => {
    if (!structuresListOverview.length) return { mainCategories: [], subCategories: {}, subCategoryCounts: {}, mainCategoryCounts: {} };
    
    const mainCategoriesSet = new Set<string>();
    const subCategoriesMap: Record<string, Set<string>> = {};
    const subCategoryCounts: Record<string, Record<string, number>> = {};
    const mainCategoryCounts: Record<string, number> = {};
    
    structuresListOverview.forEach(structure => {
      const { mainCategory, subCategory } = parseStructureType(structure.type);
      mainCategoriesSet.add(mainCategory);
      
      // Count main categories
      mainCategoryCounts[mainCategory] = (mainCategoryCounts[mainCategory] || 0) + 1;
      
      if (!subCategoriesMap[mainCategory]) {
        subCategoriesMap[mainCategory] = new Set();
        subCategoryCounts[mainCategory] = {};
      }
      if (subCategory) {
        subCategoriesMap[mainCategory].add(subCategory);
        subCategoryCounts[mainCategory][subCategory] = (subCategoryCounts[mainCategory][subCategory] || 0) + 1;
      }
    });
    
    const mainCategories = Array.from(mainCategoriesSet).sort();
    const subCategories: Record<string, string[]> = {};
    
    Object.keys(subCategoriesMap).forEach(main => {
      // Sort subcategories by count (descending), then alphabetically
      subCategories[main] = Array.from(subCategoriesMap[main]).sort((a, b) => {
        const countA = subCategoryCounts[main][a] || 0;
        const countB = subCategoryCounts[main][b] || 0;
        if (countB !== countA) {
          return countB - countA; // Sort by count descending
        }
        return a.localeCompare(b); // Then alphabetically
      });
    });
    
  // Debug logs removed for production cleanliness
    
    return { mainCategories, subCategories, subCategoryCounts, mainCategoryCounts };
  }, [structuresListOverview]);

  // Reset sub-category when main category changes
  useEffect(() => {
    setSubCategoryFilter("all");
  }, [mainCategoryFilter]);

  // Calculate accessibility compliance by category for chart
  const categoryComplianceData = useMemo(() => {
    if (!structuresListOverview.length) {
      return [];
    }
    
    const categoryScores: Record<string, { total: number; count: number }> = {};
    
    structuresListOverview.forEach((structure, index) => {
      const { mainCategory } = parseStructureType(structure.type);
      
      if (!categoryScores[mainCategory]) {
        categoryScores[mainCategory] = { total: 0, count: 0 };
      }
      
      // Calculate average accessibility score for this structure
      const accessibility = structure.accessibility || {};
      const fallbackAverage = getVerticalAverage(accessibility);
      const averageScore = typeof structure.totalScorePercent === 'number' ? structure.totalScorePercent : fallbackAverage;
      
      // Debug first few structures
      // Debug logs removed for production cleanliness
      
      categoryScores[mainCategory].total += averageScore;
      categoryScores[mainCategory].count += 1;
    });
    
    // Convert to chart data format
    const chartData = Object.entries(categoryScores).map(([category, data]) => ({
      category: category.charAt(0).toUpperCase() + category.slice(1),
      score: data.count > 0 ? data.total / data.count : 0,
      count: data.count
    })).sort((a, b) => b.score - a.score); // Sort by score descending
    
  // Debug logs removed for production cleanliness
    
    return chartData;
  }, [structuresListOverview]);

  // Calculate optimal Y-axis range for better visualization
  const chartYAxisDomain = useMemo(() => {
    if (!categoryComplianceData.length) return [0, 100];
    
    const scores = categoryComplianceData.map(d => d.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const range = maxScore - minScore;
    
    // Add padding (20% of range, minimum 5 points)
    const padding = Math.max(range * 0.2, 5);
    const yMin = Math.max(0, minScore - padding);
    const yMax = Math.min(100, maxScore + padding);
    
  // Debug logs removed for production cleanliness
    
    return [Math.floor(yMin), Math.ceil(yMax)];
  }, [categoryComplianceData]);

  // Filtered and granular chart data based on user selections
  const filteredChartData = useMemo(() => {
    if (!structuresListOverview.length) return [];

    // Filter structures by category if needed
    let filteredStructures = structuresListOverview;
    if (chartCategoryFilter !== "all") {
      filteredStructures = structuresListOverview.filter(structure => {
        const { mainCategory } = parseStructureType(structure.type);
        return mainCategory === chartCategoryFilter;
      });
    }

    if (chartGranularity === "category") {
      // Show main categories only
      const categoryScores: Record<string, { total: number; count: number }> = {};
      
      filteredStructures.forEach(structure => {
        const { mainCategory } = parseStructureType(structure.type);
        
        if (!categoryScores[mainCategory]) {
          categoryScores[mainCategory] = { total: 0, count: 0 };
        }
        
        const accessibility = structure.accessibility || {};
        const fallbackAverage = getVerticalAverage(accessibility);
        const averageScore = typeof structure.totalScorePercent === 'number' ? structure.totalScorePercent : fallbackAverage;
        categoryScores[mainCategory].total += averageScore;
        categoryScores[mainCategory].count += 1;
      });

      return Object.entries(categoryScores).map(([category, data]) => ({
        category: category.charAt(0).toUpperCase() + category.slice(1),
        score: data.count > 0 ? data.total / data.count : 0,
        count: data.count
      })).sort((a, b) => b.score - a.score);

    } else {
      // Show subcategories
      const subCategoryScores: Record<string, { total: number; count: number }> = {};
      
      filteredStructures.forEach(structure => {
        const { mainCategory, subCategory } = parseStructureType(structure.type);
        const fullCategory = subCategory ? `${mainCategory}: ${subCategory}` : mainCategory;
        
        if (!subCategoryScores[fullCategory]) {
          subCategoryScores[fullCategory] = { total: 0, count: 0 };
        }
        
        const accessibility = structure.accessibility || {};
        const fallbackAverage = getVerticalAverage(accessibility);
        const averageScore = typeof structure.totalScorePercent === 'number' ? structure.totalScorePercent : fallbackAverage;
        subCategoryScores[fullCategory].total += averageScore;
        subCategoryScores[fullCategory].count += 1;
      });

      return Object.entries(subCategoryScores).map(([category, data]) => ({
        category: category,
        score: data.count > 0 ? data.total / data.count : 0,
        count: data.count
      })).sort((a, b) => b.score - a.score);
    }
  }, [structuresListOverview, chartCategoryFilter, chartGranularity]);

  // Update Y-axis domain for filtered data
  const filteredChartYAxisDomain = useMemo(() => {
    if (!filteredChartData.length) return [0, 100];
    
    const scores = filteredChartData.map(d => d.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const range = maxScore - minScore;
    
    // Add padding (20% of range, minimum 5 points)
    const padding = Math.max(range * 0.2, 5);
    const yMin = Math.max(0, minScore - padding);
    const yMax = Math.min(100, maxScore + padding);
    
    return [Math.floor(yMin), Math.ceil(yMax)];
  }, [filteredChartData]);

  // Generate unique types dynamically from actual data
  const uniqueTypes = useMemo(() => {
    if (!structuresListOverview.length) return [];
    const types = [...new Set(structuresListOverview.map(s => s.type).filter(Boolean))];
    return types.sort();
  }, [structuresListOverview]);

  /**
   * Map compliance percentage to color class based on thresholds:
   * 85–100: verde (green-600)
   * 70–84: verdolino (lime-600)
   * 50–69: giallo (yellow-600)
   * 41–50: arancione (orange-600)
   * 0–40: rosso (red-600)
   */
  function getComplianceColorClass(percent: number): string {
    // Clamp to 0-100
    const clamped = !Number.isNaN(percent) ? Math.max(0, Math.min(100, percent)) : 0;

    if (clamped >= 85) return 'text-green-600';
    if (clamped >= 70) return 'text-lime-600';
    if (clamped >= 50) return 'text-yellow-600';
    if (clamped >= 41) return 'text-orange-600';
    return 'text-red-600';
  }

  const getComplianceColor = (compliance: number) => {
    if (compliance >= 90) return 'text-green-600';
    if (compliance >= 80) return 'text-blue-600';
    if (compliance >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Memoize fullStructuresList to ensure stable reference for useMemo dependency
  const fullStructuresList = useMemo(() => {
    return structuresListOverview.length ? structuresListOverview : [];
  }, [structuresListOverview]);

  const structureUids = useMemo(() => {
    const uids = fullStructuresList
      .map((structure) => getCanonicalStructureId(structure))
      .filter((uid): uid is string => Boolean(uid));
    return Array.from(new Set(uids));
  }, [fullStructuresList]);

  const legacySuggestionsMap = useMemo(() => {
    const map: Record<string, unknown[]> = {};
    fullStructuresList.forEach((structure) => {
      const uid = getCanonicalStructureId(structure);
      if (!uid) return;
      const legacy = (structure as any).legacySuggestions;
      if (Array.isArray(legacy) && legacy.length > 0) {
        map[uid] = legacy;
      }
    });
    return map;
  }, [fullStructuresList]);

  useEffect(() => {
    let isActive = true;

    const loadSuggestions = async () => {
      console.log("[PortalManager] loadSuggestions() starting", { structureUidsCount: structureUids.length });
      
      if (!structureUids.length) {
        console.log("[PortalManager] No structure UIDs, skipping");
        setSuggestionsByUid({});
        return;
      }

      try {
        console.log("[PortalManager] Calling getSuggestionsForStructures with", { 
          uidsCount: structureUids.length, 
          hasLegacy: Object.keys(legacySuggestionsMap).length > 0 
        });
        
        const suggestionsMap = await getSuggestionsForStructures(
          structureUids,
          Object.keys(legacySuggestionsMap).length ? legacySuggestionsMap : undefined
        );
        
        console.log("[PortalManager] getSuggestionsForStructures returned", { resultCount: Object.keys(suggestionsMap).length });
        
        if (!isActive) return;
        setSuggestionsByUid(suggestionsMap);
      } catch (error) {
        console.error("[PortalManager] Error loading suggestions", error);
        if (!isActive) return;
        setSuggestionsByUid({});
      } finally {
        // Intentionally left empty.
      }
    };

    void loadSuggestions();

    return () => {
      isActive = false;
    };
  }, [structureUids, legacySuggestionsMap]);
  
  // Update total structures count based on actual data
  const totalStructuresCount = fullStructuresList.length;
  const totalSuggestionsCount = useMemo(() => {
    return structureUids.reduce((sum, uid) => sum + (suggestionsByUid[uid]?.length ?? 0), 0);
  }, [structureUids, suggestionsByUid]);
  
  // For Portal Manager Dashboard, show ALL cached structures for the portal
  // Do NOT filter by managedStructures - that's only for Structure Manager Dashboard
  
  const filteredStructures = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    const filtered = fullStructuresList.filter((structure) => {
      const matchesSearch = !normalizedSearch || structure.name.toLowerCase().includes(normalizedSearch);
      const { mainCategory, subCategory } = parseStructureType(structure.type);
      const matchesMainCategory = mainCategoryFilter === "all" || mainCategory === mainCategoryFilter;
      const matchesSubCategory = subCategoryFilter === "all" || subCategory === subCategoryFilter;
      return matchesSearch && matchesMainCategory && matchesSubCategory;
    });

    return filtered;
  }, [fullStructuresList, searchQuery, mainCategoryFilter, subCategoryFilter]);

  const listDiagnostics = useMemo(() => {
    const buildCounts = (items: any[], getValue: (item: any) => unknown) => {
      const counts = new Map<string, number>();
      let missing = 0;

      for (const item of items) {
        const value = getValue(item);
        if (value === undefined || value === null || value === '') {
          missing += 1;
          continue;
        }
        const key = String(value);
        counts.set(key, (counts.get(key) || 0) + 1);
      }

      const duplicates = Array.from(counts.entries()).filter(([, count]) => count > 1);
      const topDuplicates = duplicates
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([value, count]) => ({ value, count }));

      return {
        total: items.length,
        unique: counts.size,
        missing,
        duplicates: duplicates.length,
        topDuplicates
      };
    };

    const items = fullStructuresList;
    const idCounts = {
      id_backend: buildCounts(items, (item) => item.id_backend),
      backend_id: buildCounts(items, (item) => item.backend_id),
      id: buildCounts(items, (item) => item.id),
      slug: buildCounts(items, (item) => item.slug)
    };

    const firstItems = items.slice(0, 20).map((item) => ({
      name: item.name,
      id_backend: item.id_backend,
      backend_id: item.backend_id,
      id: item.id,
      slug: item.slug,
      scoresPresence: item.scoresPresence
    }));

    return {
      total: items.length,
      idCounts,
      firstItems
    };
  }, [fullStructuresList]);

  const diagnosticsKey = useMemo(() => {
    return `${listDiagnostics.total}|${filteredStructures.length}|${searchQuery}|${mainCategoryFilter}|${subCategoryFilter}`;
  }, [listDiagnostics.total, filteredStructures.length, searchQuery, mainCategoryFilter, subCategoryFilter]);

  useEffect(() => {
    console.group('Portal Manager List Diagnostics');
    console.log('List length (total):', listDiagnostics.total);
    console.log('List length (filtered):', filteredStructures.length);
    console.log('ID candidate counts:', listDiagnostics.idCounts);
    console.log('First 20 items:', listDiagnostics.firstItems);
    console.groupEnd();
  }, [diagnosticsKey, listDiagnostics, filteredStructures.length]);
  
  // Debug logs removed for production cleanliness

  // Show loading state for entire dashboard when data isn't ready
  const isLoading = initialLoading || !portalKPIs;

  const renderLoadingDashboard = () => (
    <div className="space-y-6">
      {/* Loading Introduction */}
      <Card className="bg-gradient-to-r from-orange-100 via-amber-50 to-yellow-100 border-orange-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-travelin flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-5 w-32" />
              </div>
            </div>
            <div className="text-right">
              <Skeleton className="h-10 w-16 mb-1" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="text-6xl">
              
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading KPI Cards */}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="min-w-[260px] flex-shrink-0">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <Skeleton className="w-12 h-12 rounded-lg" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-16 mb-2" />
                    <Skeleton className="h-6 w-12 mb-1" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* Loading Performance Trends */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>

      {/* Loading Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index}>
            <CardHeader>
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="p-3 border rounded-lg">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderDetailsSkeletonList = () => (
    <>
      {Array.from({ length: 4 }).map((_, index) => (
        <Card key={`details-skeleton-${index}`} className={isVisitBergamo ? "bg-[#111] border border-[#1c1c1c]" : ""}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-4 w-32 mb-3" />
                <div className="flex flex-wrap items-center gap-4">
                  {verticalConfig.map((item) => (
                    <Skeleton key={item.key} className="h-4 w-16" />
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Skeleton className="h-8 w-44" />
                <Skeleton className="h-8 w-32" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );

  const renderContent = () => {
    // Show loading dashboard for both overview and details when data isn't ready
    if (isLoading) {
      return renderLoadingDashboard();
    }

    // Authentication error - user not logged in
    if (portalStructuresError?.code === 'unauthenticated') {
      return (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-700">Autenticazione richiesta</CardTitle>
            <CardDescription className="text-amber-700">
              Devi effettuare il login per visualizzare le strutture del portale.
            </CardDescription>
          </CardHeader>
        </Card>
      );
    }

    // Permission error - user logged in but insufficient permissions
    if (portalStructuresError?.code === 'permission-denied') {
      return (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Permessi insufficienti</CardTitle>
            <CardDescription className="text-red-700">
              Permessi insufficienti per leggere structures (Portal Manager)
            </CardDescription>
          </CardHeader>
          {onRetry && (
            <CardContent>
              <Button 
                variant="outline" 
                onClick={onRetry}
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                Riprova
              </Button>
            </CardContent>
          )}
        </Card>
      );
    }

    // Missing index error - server configuration issue
    if (portalStructuresError?.code === 'missing-index') {
      return (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-700">Indice non disponibile</CardTitle>
            <CardDescription className="text-amber-700">
              Indice mancante sul backoffice RO. Riprova piu tardi o contatta il supporto.
            </CardDescription>
          </CardHeader>
          {onRetry && (
            <CardContent>
              <Button 
                variant="outline" 
                onClick={onRetry}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                Riprova
              </Button>
            </CardContent>
          )}
        </Card>
      );
    }

    // Valid empty state - authenticated, no errors, but no structures found
    if (!structuresListOverview.length) {
      return renderLoadingDashboard();
    }

    switch (activeSection) {
      case "overview":
        return (
          <div className="space-y-6">
            {/* Territory and Facilities Introduction */}
            <Introduction
              title={portalName}
              subtitle="Provincia di Bergamo"
              leftIcon={<Building2 className="w-8 h-8 text-white" />}
              rightEmoji=""
              stat={{ 
                value: totalStructuresCount, 
                label: "Strutture totali" 
              }}
            // gradientClassName="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-blue-200" // optional override
            />

            <div className="flex gap-4 overflow-x-auto pb-2">
              {/* KPI Overview - Accessibility Performance by Category */}
              {kpis.map((kpi) => (
                <div key={kpi.label} className="min-w-[260px] flex-shrink-0">
                  <KPIStatCard
                    icon={kpi.icon}
                    label={kpi.label}
                    value={kpi.value}
                    delta={kpi.delta}
                    caption={`Media su ${totalStructuresCount} strutture`}
                    iconBgClassName={kpi.iconBgClassName}
                    valueClassName={kpi.valueClassName}
                  // Optional: force a downtrend and coloring
                  // trendDirection="down"
                  // deltaClassName="text-red-600"
                  />
                </div>
              ))}
            </div> 

            {/* Performance Trends */}
            <PerformanceTrendsChart data={performanceTrendsData} hasImprovements={hasImprovements} />

            {/* Accessibility Compliance by Category */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Conformità accessibilità per categoria</CardTitle>
                    <CardDescription>
                      Punteggi medi di conformità tra diverse categorie di strutture 
                      (Punti dati: {filteredChartData.length})
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={chartCategoryFilter} onValueChange={setChartCategoryFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filtra categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte le categorie</SelectItem>
                        {categoryData.mainCategories.map(category => (
                          <SelectItem key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={chartGranularity} onValueChange={setChartGranularity}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Vista" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="category">Per categoria</SelectItem>
                        <SelectItem value="subcategory">Per sottocategoria</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredChartData.length > 0 ? (
                  <div className="w-full" style={{ height: '300px' }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={filteredChartData} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="category" 
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                        />
                        <YAxis 
                          domain={filteredChartYAxisDomain}
                          tick={{ fontSize: 12 }}
                          label={{ value: 'Punteggio (%)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          formatter={(value: any) => [`${Math.round(value)}%`, 'Punteggio medio']}
                          labelFormatter={(label: any) => `${label}`}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px'
                          }}
                        />
                        <Bar 
                          dataKey="score" 
                          radius={[4, 4, 0, 0]}
                        >
                          {filteredChartData.map((entry: any, index: number) => {
                            const colorMap: Record<string, string> = {
                              'text-green-600': '#10b981',
                              'text-lime-600': '#84cc16',
                              'text-yellow-600': '#eab308',
                              'text-orange-600': '#ea580c',
                              'text-red-600': '#dc2626',
                            };
                            const colorClass = getComplianceColorClass(entry.score);
                            const fill = colorMap[colorClass] || '#ef4444';
                            return <Cell key={`cell-${index}`} fill={fill} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <div className="text-l mb-2">Nessun dato di conformità disponibile</div>
                      <div className="text-s">Il grafico apparirà quando i dati delle strutture saranno caricati</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Popular Improvement Suggestions */}
            {/* <PopularSuggestions onViewMore={onViewImprovementSuggestions} /> */}

            {/* Facility Performance Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Top Performers */}
              <TopPerformersCard
                structures={fullStructuresList}
                onViewPassport={onViewPassport}
              />

              {/* Needs Attention */}
              <NeedsAttentionCard
                structures={fullStructuresList}
                onViewPassport={onViewPassport}
              />

              {/* Quick Stats */}
              <PortalOverviewCard
                structures={fullStructuresList}
                portalName={portalName}
                totalSuggestions={totalSuggestionsCount}
              />
            </div>

            {/* Action Items */}
            {/* <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <CheckCircle className="w-5 h-5" />
                  Recommended Actions
                </CardTitle>
                <CardDescription className="text-blue-700">
                  Priority improvements based on performance analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">Focus on Sensoriale Accessibility</h4>
                    <p className="text-s text-blue-800 mb-3">
                      Average score of 76% indicates opportunity for improvement across facilities
                    </p>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                      View Suggestions
                    </Button>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">Expand Popular Solutions</h4>
                    <p className="text-s text-blue-800 mb-3">
                      78% adoption rate for motoria ramps shows high success potential
                    </p>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                      Bulk Purchase
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card> */}
          </div>
        );

      case "details":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Dettagli strutture</CardTitle>
                    <CardDescription>Informazioni dettagliate sulle singole strutture</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Cerca strutture..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-64"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {isLoadingStructures ? (
                    // LOADING STATE
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center animate-pulse">
                        <Building2 className="w-6 h-6 text-orange-600" />
                      </div>
                      <h3 className="text-l font-medium mb-2 text-gray-700">Caricamento strutture…</h3>
                      <p className="text-muted-foreground">
                        Attendere mentre carichiamo i dati per <strong>{portalName}</strong>
                      </p>
                      <div className="mt-4 flex justify-center gap-2">
                        <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  ) : structuresLoadError ? (
                    // ERROR STATE
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      </div>
                      <h3 className="text-l font-medium mb-2 text-red-900">Errore nel caricamento</h3>
                      <p className="text-red-700 mb-4">
                        {structuresLoadError}
                      </p>
                      <Button
                        onClick={() => {
                          setStructuresLoadError(null);
                          setStructuresListOverview([]);
                          setIsLoadingStructures(true);
                          // Trigger reload by updating state
                          setTimeout(() => {
                            if (portalStructureCache && portalName) {
                              const cachedStructures = getStructuresForPortal(portalStructureCache, portalName);
                              const transformedStructures = cachedStructures.map((structure, index) => {
                                const meta = structure._meta || {};
                                const scoresPresence = getScoresPresence(structure);
                                let name = meta.name || extractStructureName(structure);
                                let type = meta.type || extractStructureType(structure);
                                let locality = meta.locality || extractLocality(structure);
                                let compliance = meta.compliance || calculateCompliance(structure);
                                let accessibility = meta.accessibility || extractAccessibilityScoresFromStructure(structure);
                                let totalScorePercent = getTotalScorePercent(structure);
                                
                                return {
                                  id: structure.id || String(index + 1),
                                  id_backend: (structure as any).id_backend,
                                  backend_id: (structure as any).backend_id,
                                  slug: (structure as any)._filename || meta.filename || meta.slug || structure.id || '',
                                  name: name || `Struttura ${index + 1}`,
                                  type: type || 'Sconosciuto',
                                  compliance: compliance,
                                  locality: locality || '',
                                  accessibility: accessibility,
                                  totalScorePercent: totalScorePercent,
                                  scoresPresence: scoresPresence,
                                  legacySuggestions: Array.isArray((structure as any).suggestions)
                                    ? (structure as any).suggestions
                                    : undefined
                                };
                              });
                              setStructuresListOverview(transformedStructures);
                              setIsLoadingStructures(false);
                            }
                          }, 500);
                        }}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        Riprova
                      </Button>
                    </div>
                  ) : filteredStructures.length > 0 ? (
                    // LIST STATE
                    filteredStructures.map((structure, index) => (
                      <Card key={getStableStructureKey(structure, index)} className={`shadow-sm ${isVisitBergamo ? "bg-[#111] border border-[#1c1c1c]" : ""}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className={`font-medium `}>{structure.name}</h4>
                                <Badge variant="outline" className="text-ss">{structure.type}</Badge>
                                {(() => {
                                  const structureUid = getCanonicalStructureId(structure);
                                  const suggestionsCount = structureUid
                                    ? (suggestionsByUid[structureUid]?.length ?? 0)
                                    : 0;
                                  return (
                                    <Badge variant="secondary" className="text-ss">
                                      Suggerimenti: {suggestionsCount}
                                    </Badge>
                                  );
                                })()}
                              </div>
                              <div className={`flex items-center gap-4 text-s mb-2 ${isVisitBergamo ? "text-white/70" : "text-muted-foreground"}`}>
                                <span>- {structure.locality}</span>
                              </div>
                              <div className="flex flex-wrap items-center gap-4 text-ss">
                                {verticalConfig.map((item) => {
                                  const score = structure.accessibility?.[item.key] ?? null;
                                  const scoreLabel = score === null ? 'N/D' : `${score}%`;
                                  const scoreClassName = score === null ? 'text-muted-foreground' : getComplianceColorClass(score);
                                  return (
                                    <div key={item.key} className="flex items-baseline gap-2">
                                      <span className={`font-medium ${scoreClassName}`}>
                                        {scoreLabel}
                                      </span>
                                      <span className="text-muted-foreground">{item.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="flex flex-col items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => onViewPassport && onViewPassport(structure.id_backend || structure.backend_id || structure.id || structure.slug)}
                                className={isVisitBergamo ? "border-[#ec2b3c] text-[#ec2b3c] hover:bg-[#ec2b3c] hover:text-white" : undefined}
                              >
                                Visualizza 'Passaporto di Accessibilità'
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => onViewMonitor && onViewMonitor(structure, portalName)}
                                disabled
                                title="Temporaneamente disattivato"
                                className={isVisitBergamo ? "border-[#ec2b3c] text-[#ec2b3c] hover:bg-[#ec2b3c] hover:text-white" : undefined}
                              >
                                Visualizza 'monitora'
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    renderDetailsSkeletonList()
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "monitora":
        // V2: Use new service endpoint for suggestions summary
        if (isSuggestionsV2Enabled()) {
          return (
            <PortalMonitorV2
              portalName={portalName}
              onOpenStructure={(structureId) => {
                // Find structure by ID and open it
                const structure = fullStructuresList.find(s => 
                  getCanonicalStructureId(s) === structureId
                );
                if (structure && onViewPassport) {
                  const slug = structure.slug || structure.id;
                  if (slug) {
                    onViewPassport(slug);
                  }
                }
              }}
            />
          );
        }
        
        // Legacy: fallback to old implementation (or show message)
        return (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-l font-semibold mb-2">Monitora V2 non disponibile</h3>
                <p className="text-s text-muted-foreground">
                  Attiva VITE_SUGGESTIONS_V2=true per utilizzare la nuova dashboard di monitoraggio.
                </p>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return <div>Seleziona una sezione dalla navigazione</div>;
    }
  };

  return (
    <div className={isVisitBergamo ? "min-h-screen flex w-full flex-col bg-[#f5f5f5]" : "min-h-screen flex w-full flex-col bg-gradient-to-br from-orange-50 via-white to-amber-50"}>
      {/* PortalHeader */}
      <PortalHeader
        activeSection={activeSection as "overview" | "details" | "monitora"}
        setActiveSection={setActiveSection}
        onGoHome={onGoHome}                // e.g. setCurrentView("home")
        onPortalSettings={() => {/* open settings */ }}
        onSwitchToStructure={onSwitchToStructure}
        onLogout={onLogout}
        portalName={portalName}
        derivedRoleLabel="Gestore Portale"
        user={user}
        branding={isVisitBergamo ? "visit-bergamo" : "default"}
      />


      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
